import { SDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import { SDUIImageElementRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Image/SDUIImageElementRenderer";
import { SDUITileInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { SDUITypographyRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer-visitor-shared/lib/verticalAspectRatio";
import { IOptimizeImageOptions } from "@faire/web/common/images/optimizeImages";
import { Column } from "@faire/web/slate/Layout";
import { cn } from "@faire/web/tools/cn";
import { IImageTile } from "@faire/web-api--source/indigofair/data/IImageTile";
import React from "react";

const SQUARE_ASPECT_RATIO = 1;
const LANDSCAPE_ASPECT_RATIO = 16 / 9;
const IMAGE_TILE_WIDTH = "100%";
const DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH = 250;

export const SDUIImageTileRenderer = ({
  tileElement,
  loggingData,
  renderBehaviour,
}: SDUITileInstanceRendererProps) => {
  const { on_click, image_tile } = tileElement;
  if (!image_tile) {
    return null;
  }
  const { style, image_element, below_image_title } = image_tile;
  if (!image_element || !style?.variant) {
    return null;
  }
  const { variant, animation_on_hover } = style;

  const aspectRatio = SDUIAspectRatioVariantMap[variant];
  const imageStyles = {
    aspectRatio: aspectRatio,
    width: IMAGE_TILE_WIDTH,
    height: "auto",
    isCircular: isImageCircular(variant),
  };

  const optimizeImageOptionsWidth =
    image_element.image?.width ?? DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH;
  const optimizeImageOptionsHeight =
    image_element.image?.height ??
    DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH / aspectRatio;

  const optimizeImageOptions: IOptimizeImageOptions = {
    resizeMethod: "fill",
    width: optimizeImageOptionsWidth,
    height: optimizeImageOptionsHeight,
  };

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logLegacyClickEvent
    >
      <Column
        className={cn(
          isImageCircular(variant)
            ? getCircularTileWrapperClassName(variant)
            : "gap-3",
          animation_on_hover?.includes(IImageTile.ImageTileAnimation.UNDERLINE)
            ? "animate-underline-text-target"
            : null,
          animation_on_hover?.includes(IImageTile.ImageTileAnimation.ZOOM)
            ? "animate-image-zoom-target"
            : null
        )}
      >
        <MaybeCircleTileImageContainer variant={variant}>
          <SDUIImageElementRenderer
            imageElement={image_element}
            loggingData={loggingData}
            renderBehaviour={renderBehaviour}
            imageStyles={imageStyles}
            optimizeImageOptions={optimizeImageOptions}
          />
        </MaybeCircleTileImageContainer>
        {below_image_title ? (
          <SDUITypographyRenderer
            textElement={below_image_title}
            loggingData={loggingData}
          />
        ) : null}
      </Column>
    </SDUIOnClickHandler>
  );
};

const MaybeCircleTileImageContainer = ({
  variant,
  children,
}: {
  variant: keyof typeof IImageTile.ImageTileVariant;
  children: React.ReactNode;
}) => {
  if (isImageCircular(variant)) {
    return <div className="w-[80px] md:w-[88px]">{children}</div>;
  } else {
    return children;
  }
};

const isImageCircular = (
  variant: keyof typeof IImageTile.ImageTileVariant
): boolean => {
  return (
    variant === IImageTile.ImageTileVariant.CIRCLE_TILE ||
    variant === IImageTile.ImageTileVariant.SMALL_CIRCLE_TILE
  );
};

const isVariantCircleTile = (
  variant: keyof typeof IImageTile.ImageTileVariant
) => variant === IImageTile.ImageTileVariant.CIRCLE_TILE;

const getCircularTileWrapperClassName = (
  variant: keyof typeof IImageTile.ImageTileVariant
): string => {
  return isVariantCircleTile(variant)
    ? "items-center gap-2 w-[103.5px] md:gap-0 md:w-[112px] lg:gap-0 lg:w-[124px]"
    : "items-center w-full gap-3";
};

const SDUIAspectRatioVariantMap: Record<IImageTile.ImageTileVariant, number> = {
  [IImageTile.ImageTileVariant.IMAGE_TILE_VARIANT_UNKNOWN]: SQUARE_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.CIRCLE_TILE]: SQUARE_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.SMALL_CIRCLE_TILE]: SQUARE_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.SQUARE_TILE]: SQUARE_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.PORTRAIT_TILE]: VERTICAL_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.LANDSCAPE_TILE]: LANDSCAPE_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.LANDSCAPE_IMAGE_CUTOUT]: LANDSCAPE_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.THIN_LANDSCAPE_TILE]: LANDSCAPE_ASPECT_RATIO,
  [IImageTile.ImageTileVariant.EXTRA_THIN_LANDSCAPE_TILE]:
    LANDSCAPE_ASPECT_RATIO,
};
